import type {DbFeature, DbFeatureToolSetting} from "../interfaces/Feature";

/**
 * Memory slimming for dbFeatures coming from the backend.
 *
 * WHY "= undefined" and not `delete` / a new object:
 *  - `delete` switches the object to dictionary mode in V8: every remaining property costs ~3 words
 *    instead of 1 and every property read gets slower. With ~40k features that is worse than doing nothing.
 *  - Creating a new object (`{...dbFeature, prop: undefined}` / rest destructuring) changes the identity.
 *    Lots of code still holds the raw response objects after layout.loadNewFeatures(response.data)
 *    (markup, cart restore, related/bundled arrays), so identity must be kept.
 *  - Setting a property to `undefined` keeps the hidden class (fast), keeps identity, and releases the
 *    VALUE (strings, arrays, nested objects) to the GC. The empty slot costs 8 bytes - that's the trade-off.
 *    JSON.stringify skips undefined values, so payloads/logs don't change.
 *
 * Only properties that are NOT read anywhere in src/ are released. Lixil servers send lixil* names,
 * so this is a blacklist (unknown keys are left untouched), not a whitelist.
 */

/** top level dbFeature properties that are never read by the frontend */
export const UNUSED_DB_FEATURE_PROPERTIES = [
  "isPlaceholder",
  "isShadow",
  "shippingMetrics",
  "productWeight",
  "fabricator_id",
  "showCostInFrontEnd",
  "fabricatorMaxQuantity",
  "galleryEnabled",
  "metaDataString", // raw JSON of `metaData` - can be as big as metaData itself
  "slabSqftOverridePrice",
  "profilePriceOverride",
] as const;

/** s3Bindings[] properties that are never read (entityID, layout, path, bucketName, visualizer, edge are used) */
const UNUSED_S3_BINDING_PROPERTIES = ["entityType", "itemType"] as const;

/**
 * toolSettings are interned: only {tool, enableVisualizer, enableQuotingTool} are read
 * (displayLogic.isFeatureEnabled, super admin popup). Thousands of features share a handful of combinations,
 * so they share ONE array instance per combination (was ~5 objects with dates/ids per feature).
 * NOTE: shared arrays must never be mutated - SuperAdminToolSettingsPopup copies them before editing.
 */
const internedToolSettings = new Map<string, DbFeatureToolSetting[]>();

export const getInternedToolSettings = (toolSettings: Array<Partial<DbFeatureToolSetting>>): DbFeatureToolSetting[] => {
  let key = "";
  for (let i = 0; i < toolSettings.length; i++) {
    const setting = toolSettings[i];
    if (!setting) {
      continue;
    }
    key += setting.tool + (setting.enableVisualizer ? "1" : "0") + (setting.enableQuotingTool ? "1" : "0") + "|";
  }
  let interned = internedToolSettings.get(key);
  if (!interned) {
    interned = [];
    for (let i = 0; i < toolSettings.length; i++) {
      const setting = toolSettings[i];
      if (!setting) {
        continue;
      }
      interned.push({
        tool: setting.tool,
        enableVisualizer: !!setting.enableVisualizer,
        enableQuotingTool: !!setting.enableQuotingTool,
      });
    }
    internedToolSettings.set(key, interned);
  }
  return interned;
};

/**
 * thirdPartyAR3DUrls holds the same urls that are the keys of thirdPartyAR3DMetaData (cabinet boxes have
 * dozens of door style urls each). Object keys are internalized strings, the array values are separate
 * copies, so we swap the array values for the key strings - same content, one copy in memory.
 * @param dbFeature {DbFeature}
 */
export const shareAr3dUrlStrings = (dbFeature: DbFeature): void => {
  const urls = dbFeature.thirdPartyAR3DUrls;
  const metaData = dbFeature.thirdPartyAR3DMetaData;
  if (!Array.isArray(urls) || !urls.length || !metaData || typeof metaData !== "object") {
    return;
  }
  const keys = Object.keys(metaData);
  if (!keys.length) {
    return;
  }
  const keyByValue = new Map<string, string>();
  for (let i = 0; i < keys.length; i++) {
    keyByValue.set(keys[i], keys[i]);
  }
  for (let i = 0; i < urls.length; i++) {
    const sharedKey = keyByValue.get(urls[i]);
    if (sharedKey !== undefined) {
      urls[i] = sharedKey;
    }
  }
};

/**
 * Releases the unused data of a dbFeature IN PLACE (identity is kept). Safe to call more than once.
 * @param dbFeature {DbFeature}
 * @returns {DbFeature} the same object
 */
export const slimDbFeature = <T extends DbFeature>(dbFeature: T): T => {
  if (!dbFeature || (dbFeature as {_slimmed?: boolean})._slimmed) {
    return dbFeature;
  }
  const raw = dbFeature as unknown as Record<string, unknown>;
  for (let i = 0; i < UNUSED_DB_FEATURE_PROPERTIES.length; i++) {
    const property = UNUSED_DB_FEATURE_PROPERTIES[i];
    if (raw[property] !== undefined) {
      raw[property] = undefined;
    }
  }
  if (Array.isArray(dbFeature.toolSettings)) {
    dbFeature.toolSettings = getInternedToolSettings(dbFeature.toolSettings);
  }
  if (Array.isArray(dbFeature.s3Bindings)) {
    for (let i = 0; i < dbFeature.s3Bindings.length; i++) {
      const binding = dbFeature.s3Bindings[i] as unknown as Record<string, unknown>;
      if (!binding) {
        continue;
      }
      for (let j = 0; j < UNUSED_S3_BINDING_PROPERTIES.length; j++) {
        if (binding[UNUSED_S3_BINDING_PROPERTIES[j]] !== undefined) {
          binding[UNUSED_S3_BINDING_PROPERTIES[j]] = undefined;
        }
      }
    }
  }
  shareAr3dUrlStrings(dbFeature);
  (dbFeature as {_slimmed?: boolean})._slimmed = true;
  return dbFeature;
};
